/**
 * Generated by orval v7.10.0 🍺
 * Do not edit manually.
 * Fintecy Investors API
 * API for managing investors, accounts, and transactions in the Fintecy platform
 * OpenAPI spec version: 1.0.0
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';

import type {
  AntiPhishingSettingsRequest,
  AntiPhishingSettingsResponse,
  BackupCodesResponse,
  Error,
  MagicLinkSentResponse,
  SendMagicLinkBody,
  Session,
  SetPrimary2FARequest,
  SetPrimary2FAResponse,
  TwoFactorDisableRequest,
  TwoFactorDisableResponse,
  TwoFactorMethodDisableRequest,
  TwoFactorMethodEnableRequest,
  TwoFactorMethodVerifyRequest,
  TwoFactorMethodsResponse,
  TwoFactorSetupRequest,
  TwoFactorSetupResponse,
  TwoFactorStatusResponse,
  TwoFactorVerifyRequest,
  TwoFactorVerifyResponse,
  ValidateBackupCodeRequest,
  ValidateBackupCodeResponse,
  VerifyMagicLink200,
  VerifyMagicLinkBody,
  WebAuthnAuthenticationBeginResponse,
  WebAuthnAuthenticationFinishRequest,
  WebAuthnAuthenticationFinishResponse,
  WebAuthnRegistrationBeginRequest,
  WebAuthnRegistrationBeginResponse,
  WebAuthnRegistrationFinishRequest,
  WebAuthnRegistrationFinishResponse,
} from '.././model';

import { customInstance } from '../../axios-instance';

/**
 * Authenticate user with Google OAuth token from Authorization header
 * @summary Google OAuth login
 */
export const googleLogin = (signal?: AbortSignal) => {
  return customInstance<string>({ url: `/api/auth/google`, method: 'POST', signal });
};

export const getGoogleLoginMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof googleLogin>>, TError, void, TContext>;
}): UseMutationOptions<Awaited<ReturnType<typeof googleLogin>>, TError, void, TContext> => {
  const mutationKey = ['googleLogin'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof googleLogin>>, void> = () => {
    return googleLogin();
  };

  return { mutationFn, ...mutationOptions };
};

export type GoogleLoginMutationResult = NonNullable<Awaited<ReturnType<typeof googleLogin>>>;

export type GoogleLoginMutationError = Error;

/**
 * @summary Google OAuth login
 */
export const useGoogleLogin = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof googleLogin>>, TError, void, TContext>;
  },
  queryClient?: QueryClient
): UseMutationResult<Awaited<ReturnType<typeof googleLogin>>, TError, void, TContext> => {
  const mutationOptions = getGoogleLoginMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Send a 6-digit verification code to the user's email
 * @summary Send magic link
 */
export const sendMagicLink = (sendMagicLinkBody: SendMagicLinkBody, signal?: AbortSignal) => {
  return customInstance<MagicLinkSentResponse>({
    url: `/api/auth/magic-link`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: sendMagicLinkBody,
    signal,
  });
};

export const getSendMagicLinkMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof sendMagicLink>>,
    TError,
    { data: SendMagicLinkBody },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof sendMagicLink>>,
  TError,
  { data: SendMagicLinkBody },
  TContext
> => {
  const mutationKey = ['sendMagicLink'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof sendMagicLink>>,
    { data: SendMagicLinkBody }
  > = (props) => {
    const { data } = props ?? {};

    return sendMagicLink(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type SendMagicLinkMutationResult = NonNullable<Awaited<ReturnType<typeof sendMagicLink>>>;
export type SendMagicLinkMutationBody = SendMagicLinkBody;
export type SendMagicLinkMutationError = Error;

/**
 * @summary Send magic link
 */
export const useSendMagicLink = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof sendMagicLink>>,
      TError,
      { data: SendMagicLinkBody },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof sendMagicLink>>,
  TError,
  { data: SendMagicLinkBody },
  TContext
> => {
  const mutationOptions = getSendMagicLinkMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Verify the 6-digit code sent to user's email and authenticate
 * @summary Verify magic link code
 */
export const verifyMagicLink = (verifyMagicLinkBody: VerifyMagicLinkBody, signal?: AbortSignal) => {
  return customInstance<VerifyMagicLink200>({
    url: `/api/auth/verify-magic-link`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: verifyMagicLinkBody,
    signal,
  });
};

export const getVerifyMagicLinkMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof verifyMagicLink>>,
    TError,
    { data: VerifyMagicLinkBody },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof verifyMagicLink>>,
  TError,
  { data: VerifyMagicLinkBody },
  TContext
> => {
  const mutationKey = ['verifyMagicLink'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof verifyMagicLink>>,
    { data: VerifyMagicLinkBody }
  > = (props) => {
    const { data } = props ?? {};

    return verifyMagicLink(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type VerifyMagicLinkMutationResult = NonNullable<
  Awaited<ReturnType<typeof verifyMagicLink>>
>;
export type VerifyMagicLinkMutationBody = VerifyMagicLinkBody;
export type VerifyMagicLinkMutationError = Error;

/**
 * @summary Verify magic link code
 */
export const useVerifyMagicLink = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof verifyMagicLink>>,
      TError,
      { data: VerifyMagicLinkBody },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof verifyMagicLink>>,
  TError,
  { data: VerifyMagicLinkBody },
  TContext
> => {
  const mutationOptions = getVerifyMagicLinkMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Returns a list of active user sessions
 * @summary Get user sessions
 */
export const getSessions = (signal?: AbortSignal) => {
  return customInstance<Session[]>({ url: `/api/auth/sessions`, method: 'GET', signal });
};

export const getGetSessionsQueryKey = () => {
  return [`/api/auth/sessions`] as const;
};

export const getGetSessionsQueryOptions = <
  TData = Awaited<ReturnType<typeof getSessions>>,
  TError = Error,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSessions>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetSessionsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getSessions>>> = ({ signal }) =>
    getSessions(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getSessions>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetSessionsQueryResult = NonNullable<Awaited<ReturnType<typeof getSessions>>>;
export type GetSessionsQueryError = Error;

export function useGetSessions<TData = Awaited<ReturnType<typeof getSessions>>, TError = Error>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSessions>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSessions>>,
          TError,
          Awaited<ReturnType<typeof getSessions>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSessions<TData = Awaited<ReturnType<typeof getSessions>>, TError = Error>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSessions>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSessions>>,
          TError,
          Awaited<ReturnType<typeof getSessions>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSessions<TData = Awaited<ReturnType<typeof getSessions>>, TError = Error>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSessions>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get user sessions
 */

export function useGetSessions<TData = Awaited<ReturnType<typeof getSessions>>, TError = Error>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSessions>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetSessionsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Revokes all active sessions for the authenticated user
 * @summary Revoke all sessions
 */
export const revokeAllSessions = () => {
  return customInstance<void>({ url: `/api/auth/sessions`, method: 'DELETE' });
};

export const getRevokeAllSessionsMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof revokeAllSessions>>,
    TError,
    void,
    TContext
  >;
}): UseMutationOptions<Awaited<ReturnType<typeof revokeAllSessions>>, TError, void, TContext> => {
  const mutationKey = ['revokeAllSessions'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof revokeAllSessions>>, void> = () => {
    return revokeAllSessions();
  };

  return { mutationFn, ...mutationOptions };
};

export type RevokeAllSessionsMutationResult = NonNullable<
  Awaited<ReturnType<typeof revokeAllSessions>>
>;

export type RevokeAllSessionsMutationError = Error;

/**
 * @summary Revoke all sessions
 */
export const useRevokeAllSessions = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof revokeAllSessions>>,
      TError,
      void,
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<Awaited<ReturnType<typeof revokeAllSessions>>, TError, void, TContext> => {
  const mutationOptions = getRevokeAllSessionsMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Revokes a specific session by ID
 * @summary Revoke specific session
 */
export const revokeSession = (id: string) => {
  return customInstance<void>({ url: `/api/auth/sessions/${id}`, method: 'DELETE' });
};

export const getRevokeSessionMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof revokeSession>>,
    TError,
    { id: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof revokeSession>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ['revokeSession'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof revokeSession>>, { id: string }> = (
    props
  ) => {
    const { id } = props ?? {};

    return revokeSession(id);
  };

  return { mutationFn, ...mutationOptions };
};

export type RevokeSessionMutationResult = NonNullable<Awaited<ReturnType<typeof revokeSession>>>;

export type RevokeSessionMutationError = Error;

/**
 * @summary Revoke specific session
 */
export const useRevokeSession = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof revokeSession>>,
      TError,
      { id: string },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof revokeSession>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationOptions = getRevokeSessionMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Initiates 2FA setup process for SMS or TOTP
 * @summary Setup 2FA
 */
export const setup2FA = (twoFactorSetupRequest: TwoFactorSetupRequest, signal?: AbortSignal) => {
  return customInstance<TwoFactorSetupResponse>({
    url: `/api/auth/2fa/setup`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: twoFactorSetupRequest,
    signal,
  });
};

export const getSetup2FAMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof setup2FA>>,
    TError,
    { data: TwoFactorSetupRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof setup2FA>>,
  TError,
  { data: TwoFactorSetupRequest },
  TContext
> => {
  const mutationKey = ['setup2FA'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof setup2FA>>,
    { data: TwoFactorSetupRequest }
  > = (props) => {
    const { data } = props ?? {};

    return setup2FA(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type Setup2FAMutationResult = NonNullable<Awaited<ReturnType<typeof setup2FA>>>;
export type Setup2FAMutationBody = TwoFactorSetupRequest;
export type Setup2FAMutationError = Error;

/**
 * @summary Setup 2FA
 */
export const useSetup2FA = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof setup2FA>>,
      TError,
      { data: TwoFactorSetupRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof setup2FA>>,
  TError,
  { data: TwoFactorSetupRequest },
  TContext
> => {
  const mutationOptions = getSetup2FAMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Verifies 2FA code during setup or login
 * @summary Verify 2FA code
 */
export const verify2FA = (twoFactorVerifyRequest: TwoFactorVerifyRequest, signal?: AbortSignal) => {
  return customInstance<TwoFactorVerifyResponse>({
    url: `/api/auth/2fa/verify`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: twoFactorVerifyRequest,
    signal,
  });
};

export const getVerify2FAMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof verify2FA>>,
    TError,
    { data: TwoFactorVerifyRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof verify2FA>>,
  TError,
  { data: TwoFactorVerifyRequest },
  TContext
> => {
  const mutationKey = ['verify2FA'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof verify2FA>>,
    { data: TwoFactorVerifyRequest }
  > = (props) => {
    const { data } = props ?? {};

    return verify2FA(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type Verify2FAMutationResult = NonNullable<Awaited<ReturnType<typeof verify2FA>>>;
export type Verify2FAMutationBody = TwoFactorVerifyRequest;
export type Verify2FAMutationError = Error;

/**
 * @summary Verify 2FA code
 */
export const useVerify2FA = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof verify2FA>>,
      TError,
      { data: TwoFactorVerifyRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof verify2FA>>,
  TError,
  { data: TwoFactorVerifyRequest },
  TContext
> => {
  const mutationOptions = getVerify2FAMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Disables 2FA for the authenticated user. Deprecated in favour of the per-method endpoint.
 * @deprecated
 * @summary Disable 2FA (deprecated — use /api/auth/2fa/methods/{method}/disable)
 */
export const disable2FA = (
  twoFactorDisableRequest: TwoFactorDisableRequest,
  signal?: AbortSignal
) => {
  return customInstance<TwoFactorDisableResponse>({
    url: `/api/auth/2fa/disable`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: twoFactorDisableRequest,
    signal,
  });
};

export const getDisable2FAMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof disable2FA>>,
    TError,
    { data: TwoFactorDisableRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof disable2FA>>,
  TError,
  { data: TwoFactorDisableRequest },
  TContext
> => {
  const mutationKey = ['disable2FA'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof disable2FA>>,
    { data: TwoFactorDisableRequest }
  > = (props) => {
    const { data } = props ?? {};

    return disable2FA(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type Disable2FAMutationResult = NonNullable<Awaited<ReturnType<typeof disable2FA>>>;
export type Disable2FAMutationBody = TwoFactorDisableRequest;
export type Disable2FAMutationError = Error;

/**
 * @deprecated
 * @summary Disable 2FA (deprecated — use /api/auth/2fa/methods/{method}/disable)
 */
export const useDisable2FA = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof disable2FA>>,
      TError,
      { data: TwoFactorDisableRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof disable2FA>>,
  TError,
  { data: TwoFactorDisableRequest },
  TContext
> => {
  const mutationOptions = getDisable2FAMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Returns backup codes for 2FA recovery
 * @summary Get backup codes
 */
export const getBackupCodes = (signal?: AbortSignal) => {
  return customInstance<BackupCodesResponse>({
    url: `/api/auth/2fa/backup-codes`,
    method: 'GET',
    signal,
  });
};

export const getGetBackupCodesQueryKey = () => {
  return [`/api/auth/2fa/backup-codes`] as const;
};

export const getGetBackupCodesQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackupCodes>>,
  TError = Error,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getBackupCodes>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetBackupCodesQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getBackupCodes>>> = ({ signal }) =>
    getBackupCodes(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getBackupCodes>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetBackupCodesQueryResult = NonNullable<Awaited<ReturnType<typeof getBackupCodes>>>;
export type GetBackupCodesQueryError = Error;

export function useGetBackupCodes<
  TData = Awaited<ReturnType<typeof getBackupCodes>>,
  TError = Error,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getBackupCodes>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBackupCodes>>,
          TError,
          Awaited<ReturnType<typeof getBackupCodes>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetBackupCodes<
  TData = Awaited<ReturnType<typeof getBackupCodes>>,
  TError = Error,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getBackupCodes>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBackupCodes>>,
          TError,
          Awaited<ReturnType<typeof getBackupCodes>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetBackupCodes<
  TData = Awaited<ReturnType<typeof getBackupCodes>>,
  TError = Error,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getBackupCodes>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get backup codes
 */

export function useGetBackupCodes<
  TData = Awaited<ReturnType<typeof getBackupCodes>>,
  TError = Error,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getBackupCodes>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetBackupCodesQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Generates new backup codes for 2FA recovery
 * @summary Generate new backup codes
 */
export const generateBackupCodes = (signal?: AbortSignal) => {
  return customInstance<BackupCodesResponse>({
    url: `/api/auth/2fa/backup-codes`,
    method: 'POST',
    signal,
  });
};

export const getGenerateBackupCodesMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof generateBackupCodes>>,
    TError,
    void,
    TContext
  >;
}): UseMutationOptions<Awaited<ReturnType<typeof generateBackupCodes>>, TError, void, TContext> => {
  const mutationKey = ['generateBackupCodes'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof generateBackupCodes>>,
    void
  > = () => {
    return generateBackupCodes();
  };

  return { mutationFn, ...mutationOptions };
};

export type GenerateBackupCodesMutationResult = NonNullable<
  Awaited<ReturnType<typeof generateBackupCodes>>
>;

export type GenerateBackupCodesMutationError = Error;

/**
 * @summary Generate new backup codes
 */
export const useGenerateBackupCodes = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof generateBackupCodes>>,
      TError,
      void,
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<Awaited<ReturnType<typeof generateBackupCodes>>, TError, void, TContext> => {
  const mutationOptions = getGenerateBackupCodesMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Returns the current 2FA status for the authenticated user
 * @summary Get 2FA status
 */
export const get2FAStatus = (signal?: AbortSignal) => {
  return customInstance<TwoFactorStatusResponse>({
    url: `/api/auth/2fa/status`,
    method: 'GET',
    signal,
  });
};

export const getGet2FAStatusQueryKey = () => {
  return [`/api/auth/2fa/status`] as const;
};

export const getGet2FAStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof get2FAStatus>>,
  TError = Error,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof get2FAStatus>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGet2FAStatusQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof get2FAStatus>>> = ({ signal }) =>
    get2FAStatus(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof get2FAStatus>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type Get2FAStatusQueryResult = NonNullable<Awaited<ReturnType<typeof get2FAStatus>>>;
export type Get2FAStatusQueryError = Error;

export function useGet2FAStatus<TData = Awaited<ReturnType<typeof get2FAStatus>>, TError = Error>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof get2FAStatus>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof get2FAStatus>>,
          TError,
          Awaited<ReturnType<typeof get2FAStatus>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGet2FAStatus<TData = Awaited<ReturnType<typeof get2FAStatus>>, TError = Error>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof get2FAStatus>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof get2FAStatus>>,
          TError,
          Awaited<ReturnType<typeof get2FAStatus>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGet2FAStatus<TData = Awaited<ReturnType<typeof get2FAStatus>>, TError = Error>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof get2FAStatus>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get 2FA status
 */

export function useGet2FAStatus<TData = Awaited<ReturnType<typeof get2FAStatus>>, TError = Error>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof get2FAStatus>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGet2FAStatusQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Returns status of all available 2FA methods for the user
 * @summary Get all 2FA methods status
 */
export const get2FAMethods = (signal?: AbortSignal) => {
  return customInstance<TwoFactorMethodsResponse>({
    url: `/api/auth/2fa/methods`,
    method: 'GET',
    signal,
  });
};

export const getGet2FAMethodsQueryKey = () => {
  return [`/api/auth/2fa/methods`] as const;
};

export const getGet2FAMethodsQueryOptions = <
  TData = Awaited<ReturnType<typeof get2FAMethods>>,
  TError = Error,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof get2FAMethods>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGet2FAMethodsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof get2FAMethods>>> = ({ signal }) =>
    get2FAMethods(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof get2FAMethods>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type Get2FAMethodsQueryResult = NonNullable<Awaited<ReturnType<typeof get2FAMethods>>>;
export type Get2FAMethodsQueryError = Error;

export function useGet2FAMethods<TData = Awaited<ReturnType<typeof get2FAMethods>>, TError = Error>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof get2FAMethods>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof get2FAMethods>>,
          TError,
          Awaited<ReturnType<typeof get2FAMethods>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGet2FAMethods<TData = Awaited<ReturnType<typeof get2FAMethods>>, TError = Error>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof get2FAMethods>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof get2FAMethods>>,
          TError,
          Awaited<ReturnType<typeof get2FAMethods>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGet2FAMethods<TData = Awaited<ReturnType<typeof get2FAMethods>>, TError = Error>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof get2FAMethods>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get all 2FA methods status
 */

export function useGet2FAMethods<TData = Awaited<ReturnType<typeof get2FAMethods>>, TError = Error>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof get2FAMethods>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGet2FAMethodsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Enables a specific 2FA method (sms, totp, email, passkey)
 * @summary Enable specific 2FA method
 */
export const enable2FAMethod = (
  method: 'sms' | 'totp' | 'email' | 'passkey',
  twoFactorMethodEnableRequest: TwoFactorMethodEnableRequest,
  signal?: AbortSignal
) => {
  return customInstance<TwoFactorSetupResponse>({
    url: `/api/auth/2fa/methods/${method}/enable`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: twoFactorMethodEnableRequest,
    signal,
  });
};

export const getEnable2FAMethodMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof enable2FAMethod>>,
    TError,
    { method: 'sms' | 'totp' | 'email' | 'passkey'; data: TwoFactorMethodEnableRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof enable2FAMethod>>,
  TError,
  { method: 'sms' | 'totp' | 'email' | 'passkey'; data: TwoFactorMethodEnableRequest },
  TContext
> => {
  const mutationKey = ['enable2FAMethod'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof enable2FAMethod>>,
    { method: 'sms' | 'totp' | 'email' | 'passkey'; data: TwoFactorMethodEnableRequest }
  > = (props) => {
    const { method, data } = props ?? {};

    return enable2FAMethod(method, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type Enable2FAMethodMutationResult = NonNullable<
  Awaited<ReturnType<typeof enable2FAMethod>>
>;
export type Enable2FAMethodMutationBody = TwoFactorMethodEnableRequest;
export type Enable2FAMethodMutationError = Error;

/**
 * @summary Enable specific 2FA method
 */
export const useEnable2FAMethod = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof enable2FAMethod>>,
      TError,
      { method: 'sms' | 'totp' | 'email' | 'passkey'; data: TwoFactorMethodEnableRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof enable2FAMethod>>,
  TError,
  { method: 'sms' | 'totp' | 'email' | 'passkey'; data: TwoFactorMethodEnableRequest },
  TContext
> => {
  const mutationOptions = getEnable2FAMethodMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Disables a specific 2FA method
 * @summary Disable specific 2FA method
 */
export const disable2FAMethod = (
  method: 'sms' | 'totp' | 'email' | 'passkey',
  twoFactorMethodDisableRequest: TwoFactorMethodDisableRequest,
  signal?: AbortSignal
) => {
  return customInstance<TwoFactorDisableResponse>({
    url: `/api/auth/2fa/methods/${method}/disable`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: twoFactorMethodDisableRequest,
    signal,
  });
};

export const getDisable2FAMethodMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof disable2FAMethod>>,
    TError,
    { method: 'sms' | 'totp' | 'email' | 'passkey'; data: TwoFactorMethodDisableRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof disable2FAMethod>>,
  TError,
  { method: 'sms' | 'totp' | 'email' | 'passkey'; data: TwoFactorMethodDisableRequest },
  TContext
> => {
  const mutationKey = ['disable2FAMethod'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof disable2FAMethod>>,
    { method: 'sms' | 'totp' | 'email' | 'passkey'; data: TwoFactorMethodDisableRequest }
  > = (props) => {
    const { method, data } = props ?? {};

    return disable2FAMethod(method, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type Disable2FAMethodMutationResult = NonNullable<
  Awaited<ReturnType<typeof disable2FAMethod>>
>;
export type Disable2FAMethodMutationBody = TwoFactorMethodDisableRequest;
export type Disable2FAMethodMutationError = Error;

/**
 * @summary Disable specific 2FA method
 */
export const useDisable2FAMethod = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof disable2FAMethod>>,
      TError,
      { method: 'sms' | 'totp' | 'email' | 'passkey'; data: TwoFactorMethodDisableRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof disable2FAMethod>>,
  TError,
  { method: 'sms' | 'totp' | 'email' | 'passkey'; data: TwoFactorMethodDisableRequest },
  TContext
> => {
  const mutationOptions = getDisable2FAMethodMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Verifies a specific 2FA method during setup process
 * @summary Verify specific 2FA method during setup
 */
export const verify2FAMethodSetup = (
  method: 'sms' | 'totp' | 'email' | 'passkey',
  twoFactorMethodVerifyRequest: TwoFactorMethodVerifyRequest,
  signal?: AbortSignal
) => {
  return customInstance<TwoFactorVerifyResponse>({
    url: `/api/auth/2fa/methods/${method}/verify`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: twoFactorMethodVerifyRequest,
    signal,
  });
};

export const getVerify2FAMethodSetupMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof verify2FAMethodSetup>>,
    TError,
    { method: 'sms' | 'totp' | 'email' | 'passkey'; data: TwoFactorMethodVerifyRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof verify2FAMethodSetup>>,
  TError,
  { method: 'sms' | 'totp' | 'email' | 'passkey'; data: TwoFactorMethodVerifyRequest },
  TContext
> => {
  const mutationKey = ['verify2FAMethodSetup'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof verify2FAMethodSetup>>,
    { method: 'sms' | 'totp' | 'email' | 'passkey'; data: TwoFactorMethodVerifyRequest }
  > = (props) => {
    const { method, data } = props ?? {};

    return verify2FAMethodSetup(method, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type Verify2FAMethodSetupMutationResult = NonNullable<
  Awaited<ReturnType<typeof verify2FAMethodSetup>>
>;
export type Verify2FAMethodSetupMutationBody = TwoFactorMethodVerifyRequest;
export type Verify2FAMethodSetupMutationError = Error;

/**
 * @summary Verify specific 2FA method during setup
 */
export const useVerify2FAMethodSetup = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof verify2FAMethodSetup>>,
      TError,
      { method: 'sms' | 'totp' | 'email' | 'passkey'; data: TwoFactorMethodVerifyRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof verify2FAMethodSetup>>,
  TError,
  { method: 'sms' | 'totp' | 'email' | 'passkey'; data: TwoFactorMethodVerifyRequest },
  TContext
> => {
  const mutationOptions = getVerify2FAMethodSetupMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Sets the primary 2FA method for the user
 * @summary Set primary 2FA method
 */
export const setPrimary2FAMethod = (
  setPrimary2FARequest: SetPrimary2FARequest,
  signal?: AbortSignal
) => {
  return customInstance<SetPrimary2FAResponse>({
    url: `/api/auth/2fa/primary`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: setPrimary2FARequest,
    signal,
  });
};

export const getSetPrimary2FAMethodMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof setPrimary2FAMethod>>,
    TError,
    { data: SetPrimary2FARequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof setPrimary2FAMethod>>,
  TError,
  { data: SetPrimary2FARequest },
  TContext
> => {
  const mutationKey = ['setPrimary2FAMethod'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof setPrimary2FAMethod>>,
    { data: SetPrimary2FARequest }
  > = (props) => {
    const { data } = props ?? {};

    return setPrimary2FAMethod(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type SetPrimary2FAMethodMutationResult = NonNullable<
  Awaited<ReturnType<typeof setPrimary2FAMethod>>
>;
export type SetPrimary2FAMethodMutationBody = SetPrimary2FARequest;
export type SetPrimary2FAMethodMutationError = Error;

/**
 * @summary Set primary 2FA method
 */
export const useSetPrimary2FAMethod = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof setPrimary2FAMethod>>,
      TError,
      { data: SetPrimary2FARequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof setPrimary2FAMethod>>,
  TError,
  { data: SetPrimary2FARequest },
  TContext
> => {
  const mutationOptions = getSetPrimary2FAMethodMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Returns current anti-phishing protection settings
 * @summary Get anti-phishing settings
 */
export const getAntiPhishingSettings = (signal?: AbortSignal) => {
  return customInstance<AntiPhishingSettingsResponse>({
    url: `/api/auth/anti-phishing`,
    method: 'GET',
    signal,
  });
};

export const getGetAntiPhishingSettingsQueryKey = () => {
  return [`/api/auth/anti-phishing`] as const;
};

export const getGetAntiPhishingSettingsQueryOptions = <
  TData = Awaited<ReturnType<typeof getAntiPhishingSettings>>,
  TError = Error,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getAntiPhishingSettings>>, TError, TData>
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAntiPhishingSettingsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAntiPhishingSettings>>> = ({
    signal,
  }) => getAntiPhishingSettings(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAntiPhishingSettings>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAntiPhishingSettingsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAntiPhishingSettings>>
>;
export type GetAntiPhishingSettingsQueryError = Error;

export function useGetAntiPhishingSettings<
  TData = Awaited<ReturnType<typeof getAntiPhishingSettings>>,
  TError = Error,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAntiPhishingSettings>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAntiPhishingSettings>>,
          TError,
          Awaited<ReturnType<typeof getAntiPhishingSettings>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAntiPhishingSettings<
  TData = Awaited<ReturnType<typeof getAntiPhishingSettings>>,
  TError = Error,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAntiPhishingSettings>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAntiPhishingSettings>>,
          TError,
          Awaited<ReturnType<typeof getAntiPhishingSettings>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAntiPhishingSettings<
  TData = Awaited<ReturnType<typeof getAntiPhishingSettings>>,
  TError = Error,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAntiPhishingSettings>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get anti-phishing settings
 */

export function useGetAntiPhishingSettings<
  TData = Awaited<ReturnType<typeof getAntiPhishingSettings>>,
  TError = Error,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAntiPhishingSettings>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAntiPhishingSettingsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Updates anti-phishing protection settings
 * @summary Update anti-phishing settings
 */
export const updateAntiPhishingSettings = (
  antiPhishingSettingsRequest: AntiPhishingSettingsRequest
) => {
  return customInstance<AntiPhishingSettingsResponse>({
    url: `/api/auth/anti-phishing`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: antiPhishingSettingsRequest,
  });
};

export const getUpdateAntiPhishingSettingsMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateAntiPhishingSettings>>,
    TError,
    { data: AntiPhishingSettingsRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateAntiPhishingSettings>>,
  TError,
  { data: AntiPhishingSettingsRequest },
  TContext
> => {
  const mutationKey = ['updateAntiPhishingSettings'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateAntiPhishingSettings>>,
    { data: AntiPhishingSettingsRequest }
  > = (props) => {
    const { data } = props ?? {};

    return updateAntiPhishingSettings(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateAntiPhishingSettingsMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateAntiPhishingSettings>>
>;
export type UpdateAntiPhishingSettingsMutationBody = AntiPhishingSettingsRequest;
export type UpdateAntiPhishingSettingsMutationError = Error;

/**
 * @summary Update anti-phishing settings
 */
export const useUpdateAntiPhishingSettings = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateAntiPhishingSettings>>,
      TError,
      { data: AntiPhishingSettingsRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof updateAntiPhishingSettings>>,
  TError,
  { data: AntiPhishingSettingsRequest },
  TContext
> => {
  const mutationOptions = getUpdateAntiPhishingSettingsMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Initiates WebAuthn/passkey registration process
 * @summary Begin WebAuthn registration
 */
export const beginWebAuthnRegistration = (
  webAuthnRegistrationBeginRequest: WebAuthnRegistrationBeginRequest,
  signal?: AbortSignal
) => {
  return customInstance<WebAuthnRegistrationBeginResponse>({
    url: `/api/auth/webauthn/register/begin`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: webAuthnRegistrationBeginRequest,
    signal,
  });
};

export const getBeginWebAuthnRegistrationMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof beginWebAuthnRegistration>>,
    TError,
    { data: WebAuthnRegistrationBeginRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof beginWebAuthnRegistration>>,
  TError,
  { data: WebAuthnRegistrationBeginRequest },
  TContext
> => {
  const mutationKey = ['beginWebAuthnRegistration'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof beginWebAuthnRegistration>>,
    { data: WebAuthnRegistrationBeginRequest }
  > = (props) => {
    const { data } = props ?? {};

    return beginWebAuthnRegistration(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type BeginWebAuthnRegistrationMutationResult = NonNullable<
  Awaited<ReturnType<typeof beginWebAuthnRegistration>>
>;
export type BeginWebAuthnRegistrationMutationBody = WebAuthnRegistrationBeginRequest;
export type BeginWebAuthnRegistrationMutationError = Error;

/**
 * @summary Begin WebAuthn registration
 */
export const useBeginWebAuthnRegistration = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof beginWebAuthnRegistration>>,
      TError,
      { data: WebAuthnRegistrationBeginRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof beginWebAuthnRegistration>>,
  TError,
  { data: WebAuthnRegistrationBeginRequest },
  TContext
> => {
  const mutationOptions = getBeginWebAuthnRegistrationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Completes WebAuthn/passkey registration process
 * @summary Finish WebAuthn registration
 */
export const finishWebAuthnRegistration = (
  webAuthnRegistrationFinishRequest: WebAuthnRegistrationFinishRequest,
  signal?: AbortSignal
) => {
  return customInstance<WebAuthnRegistrationFinishResponse>({
    url: `/api/auth/webauthn/register/finish`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: webAuthnRegistrationFinishRequest,
    signal,
  });
};

export const getFinishWebAuthnRegistrationMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof finishWebAuthnRegistration>>,
    TError,
    { data: WebAuthnRegistrationFinishRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof finishWebAuthnRegistration>>,
  TError,
  { data: WebAuthnRegistrationFinishRequest },
  TContext
> => {
  const mutationKey = ['finishWebAuthnRegistration'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof finishWebAuthnRegistration>>,
    { data: WebAuthnRegistrationFinishRequest }
  > = (props) => {
    const { data } = props ?? {};

    return finishWebAuthnRegistration(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type FinishWebAuthnRegistrationMutationResult = NonNullable<
  Awaited<ReturnType<typeof finishWebAuthnRegistration>>
>;
export type FinishWebAuthnRegistrationMutationBody = WebAuthnRegistrationFinishRequest;
export type FinishWebAuthnRegistrationMutationError = Error;

/**
 * @summary Finish WebAuthn registration
 */
export const useFinishWebAuthnRegistration = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof finishWebAuthnRegistration>>,
      TError,
      { data: WebAuthnRegistrationFinishRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof finishWebAuthnRegistration>>,
  TError,
  { data: WebAuthnRegistrationFinishRequest },
  TContext
> => {
  const mutationOptions = getFinishWebAuthnRegistrationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Initiates WebAuthn/passkey authentication process
 * @summary Begin WebAuthn authentication
 */
export const beginWebAuthnAuthentication = (signal?: AbortSignal) => {
  return customInstance<WebAuthnAuthenticationBeginResponse>({
    url: `/api/auth/webauthn/authenticate/begin`,
    method: 'POST',
    signal,
  });
};

export const getBeginWebAuthnAuthenticationMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof beginWebAuthnAuthentication>>,
    TError,
    void,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof beginWebAuthnAuthentication>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = ['beginWebAuthnAuthentication'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof beginWebAuthnAuthentication>>,
    void
  > = () => {
    return beginWebAuthnAuthentication();
  };

  return { mutationFn, ...mutationOptions };
};

export type BeginWebAuthnAuthenticationMutationResult = NonNullable<
  Awaited<ReturnType<typeof beginWebAuthnAuthentication>>
>;

export type BeginWebAuthnAuthenticationMutationError = Error;

/**
 * @summary Begin WebAuthn authentication
 */
export const useBeginWebAuthnAuthentication = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof beginWebAuthnAuthentication>>,
      TError,
      void,
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof beginWebAuthnAuthentication>>,
  TError,
  void,
  TContext
> => {
  const mutationOptions = getBeginWebAuthnAuthenticationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Completes WebAuthn/passkey authentication process
 * @summary Finish WebAuthn authentication
 */
export const finishWebAuthnAuthentication = (
  webAuthnAuthenticationFinishRequest: WebAuthnAuthenticationFinishRequest,
  signal?: AbortSignal
) => {
  return customInstance<WebAuthnAuthenticationFinishResponse>({
    url: `/api/auth/webauthn/authenticate/finish`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: webAuthnAuthenticationFinishRequest,
    signal,
  });
};

export const getFinishWebAuthnAuthenticationMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof finishWebAuthnAuthentication>>,
    TError,
    { data: WebAuthnAuthenticationFinishRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof finishWebAuthnAuthentication>>,
  TError,
  { data: WebAuthnAuthenticationFinishRequest },
  TContext
> => {
  const mutationKey = ['finishWebAuthnAuthentication'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof finishWebAuthnAuthentication>>,
    { data: WebAuthnAuthenticationFinishRequest }
  > = (props) => {
    const { data } = props ?? {};

    return finishWebAuthnAuthentication(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type FinishWebAuthnAuthenticationMutationResult = NonNullable<
  Awaited<ReturnType<typeof finishWebAuthnAuthentication>>
>;
export type FinishWebAuthnAuthenticationMutationBody = WebAuthnAuthenticationFinishRequest;
export type FinishWebAuthnAuthenticationMutationError = Error;

/**
 * @summary Finish WebAuthn authentication
 */
export const useFinishWebAuthnAuthentication = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof finishWebAuthnAuthentication>>,
      TError,
      { data: WebAuthnAuthenticationFinishRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof finishWebAuthnAuthentication>>,
  TError,
  { data: WebAuthnAuthenticationFinishRequest },
  TContext
> => {
  const mutationOptions = getFinishWebAuthnAuthenticationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Validates a backup code without consuming it
 * @summary Validate backup code
 */
export const validateBackupCode = (
  validateBackupCodeRequest: ValidateBackupCodeRequest,
  signal?: AbortSignal
) => {
  return customInstance<ValidateBackupCodeResponse>({
    url: `/api/auth/2fa/backup-codes/validate`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: validateBackupCodeRequest,
    signal,
  });
};

export const getValidateBackupCodeMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof validateBackupCode>>,
    TError,
    { data: ValidateBackupCodeRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof validateBackupCode>>,
  TError,
  { data: ValidateBackupCodeRequest },
  TContext
> => {
  const mutationKey = ['validateBackupCode'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof validateBackupCode>>,
    { data: ValidateBackupCodeRequest }
  > = (props) => {
    const { data } = props ?? {};

    return validateBackupCode(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type ValidateBackupCodeMutationResult = NonNullable<
  Awaited<ReturnType<typeof validateBackupCode>>
>;
export type ValidateBackupCodeMutationBody = ValidateBackupCodeRequest;
export type ValidateBackupCodeMutationError = Error;

/**
 * @summary Validate backup code
 */
export const useValidateBackupCode = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof validateBackupCode>>,
      TError,
      { data: ValidateBackupCodeRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof validateBackupCode>>,
  TError,
  { data: ValidateBackupCodeRequest },
  TContext
> => {
  const mutationOptions = getValidateBackupCodeMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
